import {ref, computed} from 'vue'
import api from '@/services/api'
import type {Notification} from '@/types/notification'

/**
 * Notification state and the polling strategy behind it.
 *
 * Why polling at all: at this scale a WebSocket connection per user buys ~30s of
 * latency on a low-urgency feed in exchange for a permanent connection table,
 * reconnection handling, and a second ingress path. The dominant cost was never
 * the request rate — it was that each request scanned the whole table. With the
 * server side now answering from a sparse index in single-digit milliseconds,
 * revalidating polls are close to free, and the remaining wins are in *not
 * sending* requests nobody is waiting for. Hence: pause when hidden, back off
 * when quiet, share one result across tabs, and let HTTP revalidation collapse
 * unchanged responses to a bodyless 304.
 *
 * See docs/NOTIFICATION_ARCHITECTURE.md for the thresholds at which a push
 * transport starts to pay for itself.
 */

const notifications = ref<Notification[]>([])
const unreadCount = ref(0)
const unreadCapped = ref(false)
const isLoading = ref(false)

const BASE_INTERVAL_MS = 60_000
const MAX_INTERVAL_MS = 300_000
const JITTER = 0.2
const SHARED_KEY = 'aiar:notifications:unread'
// Slightly under one interval, so a tab that just missed a sibling's poll still
// treats the shared value as fresh instead of duplicating the request.
const SHARED_TTL_MS = 45_000

let timer: ReturnType<typeof setTimeout> | null = null
let currentInterval = BASE_INTERVAL_MS
let quietPolls = 0
let subscribers = 0
let listenersBound = false

/** Spreads clients out so they do not all wake on the same second. */
function withJitter(ms: number): number {
    const delta = ms * JITTER
    return Math.round(ms - delta + Math.random() * delta * 2)
}

function readShared(): {count: number; capped: boolean; at: number} | null {
    try {
        const raw = localStorage.getItem(SHARED_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        return typeof parsed?.at === 'number' ? parsed : null
    } catch {
        return null
    }
}

function writeShared(count: number, capped: boolean) {
    try {
        localStorage.setItem(SHARED_KEY, JSON.stringify({count, capped, at: Date.now()}))
    } catch {
        // Private browsing or a full quota — polling still works, just per-tab.
    }
}

function applyCount(count: number, capped: boolean) {
    if (count !== unreadCount.value) {
        // Something changed, so the user is plausibly active again.
        quietPolls = 0
        currentInterval = BASE_INTERVAL_MS
    }
    unreadCount.value = count
    unreadCapped.value = capped
}

export function useNotifications() {
    const unread = computed(() => notifications.value.filter((n) => !n.read))

    async function fetchNotifications() {
        isLoading.value = true
        try {
            const res = await api.get('/api/v1/notifications')
            notifications.value = res.data
        } catch (err: any) {
            console.warn('[Notifications] Failed to fetch:', err?.message)
        } finally {
            isLoading.value = false
        }
    }

    /**
     * @param force skip the cross-tab freshness check (used on focus and after
     *              an action, where the user is waiting on an answer)
     */
    async function fetchUnreadCount({force = false} = {}) {
        if (!force) {
            const shared = readShared()
            if (shared && Date.now() - shared.at < SHARED_TTL_MS) {
                applyCount(shared.count, shared.capped)
                return
            }
        }

        try {
            const res = await api.get('/api/v1/notifications/unread-count')
            const count = Number(res.data?.count ?? 0)
            const capped = Boolean(res.data?.capped)
            applyCount(count, capped)
            writeShared(count, capped)
        } catch {
            // Transient failure is not worth surfacing on a background poll; the
            // next tick retries and the interval has already backed off.
        }
    }

    function scheduleNext() {
        if (timer) clearTimeout(timer)
        if (subscribers === 0) return

        timer = setTimeout(async () => {
            if (document.visibilityState === 'visible') {
                const before = unreadCount.value
                await fetchUnreadCount()
                quietPolls = unreadCount.value === before ? quietPolls + 1 : 0
                // Three quiet cycles in a row and the feed is clearly idle;
                // stretch the interval rather than keep asking at full rate.
                if (quietPolls >= 3) {
                    currentInterval = Math.min(currentInterval * 2, MAX_INTERVAL_MS)
                    quietPolls = 0
                }
            }
            scheduleNext()
        }, withJitter(currentInterval))
    }

    function handleVisibility() {
        if (document.visibilityState === 'visible') {
            // Coming back to the tab is the one moment freshness really matters.
            currentInterval = BASE_INTERVAL_MS
            quietPolls = 0
            fetchUnreadCount({force: true})
            scheduleNext()
        } else if (timer) {
            clearTimeout(timer)
            timer = null
        }
    }

    function handleStorage(event: StorageEvent) {
        if (event.key !== SHARED_KEY || !event.newValue) return
        try {
            const parsed = JSON.parse(event.newValue)
            applyCount(Number(parsed.count ?? 0), Boolean(parsed.capped))
        } catch {
            /* ignore malformed cross-tab payloads */
        }
    }

    function startPolling() {
        subscribers += 1
        if (!listenersBound) {
            document.addEventListener('visibilitychange', handleVisibility)
            window.addEventListener('focus', handleVisibility)
            window.addEventListener('storage', handleStorage)
            listenersBound = true
        }
        fetchUnreadCount({force: true})
        scheduleNext()
    }

    function stopPolling() {
        subscribers = Math.max(0, subscribers - 1)
        if (subscribers === 0) {
            if (timer) clearTimeout(timer)
            timer = null
            document.removeEventListener('visibilitychange', handleVisibility)
            window.removeEventListener('focus', handleVisibility)
            window.removeEventListener('storage', handleStorage)
            listenersBound = false
        }
    }

    async function markAsRead(notificationId: string) {
        try {
            await api.put(`/api/v1/notifications/${notificationId}/read`)
            const idx = notifications.value.findIndex((n) => n.id === notificationId)
            if (idx !== -1) notifications.value[idx].read = true
            applyCount(Math.max(0, unreadCount.value - 1), unreadCapped.value)
            writeShared(unreadCount.value, unreadCapped.value)
            // The optimistic decrement is a guess whenever the count was capped;
            // reconcile against the server rather than drift.
            if (unreadCapped.value) await fetchUnreadCount({force: true})
        } catch (err: any) {
            console.warn('[Notifications] Failed to mark as read:', err?.message)
            await fetchUnreadCount({force: true})
        }
    }

    async function markAllAsRead() {
        try {
            const res = await api.put('/api/v1/notifications/read-all')
            notifications.value.forEach((n) => (n.read = true))
            applyCount(0, false)
            writeShared(0, false)
            // A backlog larger than one batch leaves some unread behind.
            if (res.data?.remaining) await fetchUnreadCount({force: true})
        } catch (err: any) {
            console.warn('[Notifications] Failed to mark all as read:', err?.message)
            await fetchUnreadCount({force: true})
        }
    }

    return {
        notifications,
        unread,
        unreadCount,
        unreadCapped,
        isLoading,
        fetchNotifications,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        startPolling,
        stopPolling,
    }
}
